import React, { useState, useEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';
import { API, api } from '../config';

export default function AdminArticlesPage() {
  const [adminCategories, setAdminCategories] = React.useState([]);
  React.useEffect(() => {
    fetch(`${API}/categories`).then(r => r.json()).then(d => setAdminCategories(d.categories || [])).catch(() => {});
  }, []);

  const [articles, setArticles] = useState([]);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const contentEditorRef = useRef(null);
  const turnstileRef = useRef(null);
  
  const [turnstileToken, setTurnstileToken] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'healthcare',
    author: 'GetBenefitSavings Team',
    published: true,
    tags: []
  });

  // Fixed: formatText now uses useCallback and proper ref access
  const formatText = useCallback((command, value = null) => {
    if (contentEditorRef.current) {
      contentEditorRef.current.focus();
      document.execCommand(command, false, value);
      setFormData(prev => ({ ...prev, content: contentEditorRef.current.innerHTML }));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchArticles();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlAuth = urlParams.get("auth");
    const adminToken = localStorage.getItem("adminToken");
    if (urlAuth) {
      setPassword(urlAuth);
      setIsAuthenticated(true);
      localStorage.setItem('articles_authenticated', 'true');
    } else if (adminToken) {
      setIsAuthenticated(true);
      localStorage.setItem('articles_authenticated', 'true');
    } else if (localStorage.getItem('articles_authenticated') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Initialize Turnstile on login page
  useEffect(() => {
    if (!isAuthenticated && window.turnstile && turnstileRef.current) {
      window.turnstile.render(turnstileRef.current, {
        sitekey: '0x4AAAAAACX5zPRry72pjXTd',
        theme: 'light',
        callback: (token) => {
          setTurnstileToken(token);
        },
        'error-callback': () => {
          alert('Verification failed. Please try again.');
        }
      });
    }
  }, [isAuthenticated]);

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${API}/articles?published_only=false`);
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();

    if (!turnstileToken) {
      alert('Please complete the verification.');
      return;
    }

    if (password) {
      setIsAuthenticated(true);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/articles', formData);
      alert('Article created successfully!');
      setShowEditor(false);
      resetForm();
      fetchArticles();
    } catch (error) {
      console.error('Create error:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    console.log("Update clicked", editingArticle, formData);
    try {
      console.log("Calling API PUT");
      const response = await api.put(`/articles/${editingArticle.id}`, formData);
      if (response.data) {
        alert('Article updated successfully!');
        setShowEditor(false);
        setEditingArticle(null);
        resetForm();
        fetchArticles();
      } else {
        alert('Failed to update article');
      }
    } catch (error) {
      console.error("Update error:", error);
      alert("Error: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await api.delete(`/articles/${articleId}`);
      alert('Article deleted successfully!');
      fetchArticles();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const startEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      author: article.author,
      published: article.published,
      tags: article.tags
    });
    setShowEditor(true);
    setShowPreview(false); // Reset preview when editing
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'healthcare',
      author: 'GetBenefitSavings Team',
      published: true,
      tags: []
    });
    setEditingArticle(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, tags }));
  };

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  // Set editor content when formData.content changes
  useEffect(() => {
    if (contentEditorRef.current && formData.content && !showPreview) {
      contentEditorRef.current.innerHTML = formData.content;
    }
  }, [formData.content, showPreview]);

  // Fixed: content change handler - use functional updates
  const handleContentChange = (e) => {
    setFormData(prev => ({ ...prev, content: e.currentTarget.innerHTML }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
            <p className="text-gray-600">Articles Management Portal</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="Enter admin password"
                required
              />
            </div>

            <div ref={turnstileRef} className="mb-4"></div>

            <button
              type="submit"
              disabled={!turnstileToken}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
            >
              Login to Admin Panel
            </button>

            <div className="text-center">
              <a href="/spoleto/dashboard" className="text-sm text-gray-600 hover:text-blue-600">
                ← Back to site
              </a>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <a href="/" className="flex items-center gap-2">
                <img src="/logo.png" alt="GetBenefitSavings" className="h-8 w-auto" />
                <span className="text-lg font-bold text-gray-900">GetBenefitSavings</span>
              </a>
              <span className="text-gray-400">|</span>
              <h1 className="text-xl font-bold text-gray-900">📝 Articles</h1>
            </div>
            <div className="flex gap-4">
              <a href="/spoleto/dashboard" className="text-sm text-gray-600 hover:text-blue-600">← Back to Portal</a>
              <button
                onClick={() => { setShowEditor(true); resetForm(); }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-blue-700"
              >
                + New Article
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showEditor && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {articles.map(article => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{article.title}</div>
                      <div className="text-sm text-gray-500">{article.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-semibold uppercase">
                        {article.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                        article.published ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {article.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => startEdit(article)}
                        className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="text-red-600 hover:text-red-700 font-semibold text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {articles.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No articles yet. Create your first one!
              </div>
            )}
          </div>
        )}

        {showEditor && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingArticle ? 'Edit Article' : 'Create New Article'}
              </h2>
              <button
                onClick={() => { setShowEditor(false); resetForm(); }}
                className="text-gray-600 hover:text-gray-900"
              >
                ✕ Close
              </button>
            </div>

            <form onSubmit={editingArticle ? handleUpdate : handleCreate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    onBlur={generateSlug}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Excerpt *</label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                />
              </div>

              {/* Fixed: Content editor section - removed duplicate type, better event handling */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-semibold text-gray-700">Content *</label>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium"
                  >
                    {showPreview ? '✏️ Edit' : '👁️ Preview'}
                  </button>
                </div>

                {!showPreview && (
                  <div className="flex gap-2 mb-2 p-2 bg-gray-50 border border-gray-200 rounded flex-wrap">
                    <button type="button" onClick={() => formatText('bold')} className="px-3 py-1 bg-white border rounded hover:bg-gray-100" title="Bold">
                      <strong>B</strong>
                    </button>
                    <button type="button" onClick={() => formatText('italic')} className="px-3 py-1 bg-white border rounded hover:bg-gray-100" title="Italic">
                      <em>I</em>
                    </button>
                    <button type="button" onClick={() => formatText('formatBlock', 'h1')} className="px-3 py-1 bg-white border rounded hover:bg-gray-100 text-xl font-bold" title="Heading 1">
                      H1
                    </button>
                    <button type="button" onClick={() => formatText('formatBlock', 'h2')} className="px-3 py-1 bg-white border rounded hover:bg-gray-100 text-lg font-bold" title="Heading 2">
                      H2
                    </button>
                    <button type="button" onClick={() => formatText('formatBlock', 'h3')} className="px-3 py-1 bg-white border rounded hover:bg-gray-100 font-bold" title="Heading 3">
                      H3
                    </button>
                    <button type="button" onClick={() => formatText('insertUnorderedList')} className="px-3 py-1 bg-white border rounded hover:bg-gray-100" title="Bullet List">
                      • List
                    </button>
                    <button type="button" onClick={() => formatText('insertOrderedList')} className="px-3 py-1 bg-white border rounded hover:bg-gray-100" title="Numbered List">
                      1. List
                    </button>
                  </div>
                )}

                {showPreview ? (
                  <div
                    className="min-h-[400px] p-4 border border-gray-300 rounded-lg bg-white overflow-auto"
                    style={{
                      color: '#1f2937',
                      lineHeight: '1.6'
                    }}
                    dangerouslySetInnerHTML={{ __html: (formData.content ? marked.parse(formData.content) : '<p class="text-gray-400">No content yet...</p>') }}
                  />
                ) : (
                  <div
                    ref={contentEditorRef}
                    contentEditable={!showPreview}
                    suppressContentEditableWarning
                    onInput={handleContentChange}
                    onPaste={(e) => {
                      setTimeout(() => {
                        handleContentChange({ currentTarget: e.currentTarget });
                      }, 10);
                    }}
                    className="min-h-[400px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 font-mono text-sm overflow-auto"
                    style={{ whiteSpace: 'pre-wrap', color: '#1f2937' }}
                  />
                )}
              </div>

              {/* Add missing form fields for category, author, published, tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  >
                    {adminCategories.map(cat => (
                      <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Author</label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="published"
                    checked={formData.published}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm font-semibold text-gray-700">Published</label>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={formData.tags.join(', ')}
                    onChange={handleTagsChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="health, insurance, medicare"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditor(false); resetForm(); }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                >
                  {editingArticle ? 'Update Article' : 'Create Article'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
